import $ from 'jquery';

import ImageField from 'chairisher/component/form/fieldimage';
import ImageFieldSet from 'chairisher/component/form/fieldimageset';
import ProductUpdateCard from 'chairisher/view/product/update/card';

import { isStaff } from 'chairisher/context/auth';
import { getCategoriesTree } from 'chairisher/context/listingflow';

/**
 * View that encapsulates functionality for the "Basics" section of the Product listing flow.
 */
class BasicsCard extends ProductUpdateCard {
    constructor() {
        super('#js-basic-fields');
        /**
         * Used to traverse the DOM to select ImageFields
         *
         * @type {string}
         */
        this.imageFieldSelector = '.js-image-control';

        const $imageFields = this.$cardFieldsEl.find(this.imageFieldSelector);

        const imageFields = [];
        $imageFields.each((_, el) => {
            const imageField = new ImageField({
                $el: $(el),
                endpointUrl: '/product/image/create',
            });
            imageField.bind();
            imageFields.push(imageField);
        });

        /**
         * Instance of an ImageFieldSet that controls all ImageField instances on the page
         *
         * @type {ImageFieldSet}
         */
        this.imageFieldSet = new ImageFieldSet({
            imageFields,
            $el: this.$cardFieldsEl.find('.js-image-control-wrapper'),
        });

        /**
         * Used to traverse the DOM to select the card's completion indicator
         *
         * @type {string}
         */
        this.completionIndicatorSelector = '.js-step-basics';

        /**
         * A collection of field names that are bound as ChoiceTreeAutocomplete components
         *
         * @type {Array.<string>}
         */
        this.choiceTreeComponentNames = ['categories'];

        this.initialize();
    }

    /**
     * Sets the images and renders them.
     *
     * @param {Array.<Choice>} imageData
     */
    setImages(imageData) {
        const imageDataToSet =
            isStaff() && imageData
                ? imageData.map((item) => ({
                      ...item,
                      getParams: {
                          'aspect': 'fit',
                          'format': 'png',
                          'bg-color': 'CC4639',
                          'size': 800,
                      },
                  }))
                : imageData;

        this.imageFieldSet.setValues(imageDataToSet);
        this.imageFieldSet.setPrimaryImage();
    }

    /** @inheritdoc * */
    updateFieldsFromDataObject(data) {
        super.updateFieldsFromDataObject(data);

        const imageData = data.images;
        if (imageData.is_readonly) {
            this.imageFieldSet.disable();
        } else {
            this.imageFieldSet.enable();
        }
    }

    /** @inheritdoc */
    updateWarningsFromDataObject(data) {
        //
        // Categories
        //
        this.updateFieldWarnings($('.js-categories-warnings-wrapper'), data.categories.warnings);
    }

    /**
     * Binds actions to the various elements that comprise the Card
     */
    bind() {
        super.bind();

        //
        // Categories
        //

        this.bindChoiceTreeAutocomplete({
            areOnlyLeafNodesSelectable: true,
            dataEndpoint: '/choices/product/categories/search', // todo: (CHAIR-5561) place in JS context
            treeChoices: getCategoriesTree(),
            $queryEl: this.$cardFieldsEl.find('[name="categories"]'),
        });

        //
        // Images
        //

        const $imageFieldSet = this.imageFieldSet.getEl();

        $imageFieldSet.on('imagefieldset:imagedatachanged', (_, data) => {
            this.triggerChange({ images: data.guids });
        });

        $imageFieldSet.on('imagefieldset:imageuploadfail', (_, data) => {
            this.updateFieldErrors('images', data.errors);
        });
    }

    /**
     * Marshals a jQuery element that represents a line in the card summary
     *
     * @param {string} label The label to display
     * @param {string} values The values to display
     * @returns {jQuery}
     */
    marshalSummaryLineEl(label, values) {
        let display = values;
        if (label && label.toLowerCase() === 'photos') {
            const images = [];
            const $images = this.$cardFieldsEl.find('.js-image > img');

            $.each($images, (i) => {
                const $img = $images.eq(i).clone().attr({
                    class: 'listing-image',
                    id: '',
                });
                images.push($img[0].outerHTML);
            });

            display = images.join('');
        }

        if (display) {
            super.marshalSummaryLineEl(label, display);
        }
    }

    /**
     * Populates the list of field names in this card, excluding buttons
     */
    populateFieldNames() {
        super.populateFieldNames();
        this.fieldNames.push('images');
    }

    /**
     * Populates the list of simple field names excluding buttons
     */
    populateSimpleFieldNames() {
        this.simpleFieldNames = $.makeArray($(this.fieldNames.concat([])).not(['categories', 'images']));
    }
}

export default BasicsCard;
